import React, { useEffect, useState, useCallback, useRef } from "react";
import { dashboardApi } from "../../services/api";
import { StatCard } from "../../components/ui";
import { useAuth } from "../../contexts/AuthContext";
import { showToast } from "../../utils/toast";
import {
  ShoppingCart,
  CheckCircle2,
  Clock,
  XCircle,
  Wallet,
  Users,
  UserCheck,
  UserX,
  Star,
  Smartphone,
  ArrowUpCircle,
  ArrowDownCircle,
  Gift,
  Banknote,
  Sparkles,
  Moon,
  CreditCard,
  CircleDot,
  Activity,
  Database,
  Server,
  TrendingUp,
  CalendarDays,
  DollarSign,
  PiggyBank,
  UserPlus,
  LayoutList,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────
IST date helpers (UTC+5:30)
All date computation done in IST — not browser local time.
───────────────────────────────────────────────────────────────── */
function nowIST() {
  return new Date(Date.now() + 5.5 * 60 * 60 * 1000);
}
function isoDate(d) {
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function todayIST() { return isoDate(nowIST()); }
function yesterdayIST() {
  const d = nowIST(); d.setUTCDate(d.getUTCDate() - 1); return isoDate(d);
}
function weekStartIST() {
  const d = nowIST();
  const day = d.getUTCDay();
  d.setUTCDate(d.getUTCDate() + (day === 0 ? -6 : 1 - day));
  return isoDate(d);
}
function monthStartIST() {
  const d = nowIST(); d.setUTCDate(1); return isoDate(d);
}

/** Date range sent to backend for each filter */
function getRange(filter, custom = null) {
  if (filter === "Custom" && custom) return custom;
  const today = todayIST();
  const yesterday = yesterdayIST();
  const weekStart = weekStartIST();
  const monthStart = monthStartIST();
  switch (filter) {
    case "Today": return { startDate: today, endDate: today };
    case "Yesterday": return { startDate: yesterday, endDate: yesterday };
    case "This Week": return { startDate: weekStart, endDate: today };
    case "This Month": return { startDate: monthStart, endDate: today };
    case "All Time":
    default: return { startDate: null, endDate: null };
  }
}

/**
 * Maps UI filter label → backend filter enum.
 * Backend uses this to compute growth — eliminates Monday date-collision.
 */
function toBackendFilter(f) {
  switch (f) {
    case "Today": return "TODAY";
    case "Yesterday": return "YESTERDAY";
    case "This Week": return "THIS_WEEK";
    case "This Month": return "THIS_MONTH";
    case "All Time": return "ALL_TIME";
    default: return "CUSTOM";
  }
}

function growthLabel(filter) {
  switch (filter) {
    case "Today": return "vs yesterday";
    case "Yesterday": return "vs day before";
    case "This Week": return "vs last week";
    case "This Month": return "vs last month";
    default: return "";
  }
}

/* ── Section Title ── */
function SectionTitle({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
      <div style={{ width: 4, height: 22, borderRadius: 2, background: "#0F3D3E", flexShrink: 0 }} />
      <h2 style={{
        fontFamily: "var(--font-display)", fontSize: 16,
        color: "var(--text-primary)", fontWeight: 600,
      }}>
        {children}
      </h2>
    </div>
  );
}

/* ── Health Pill ── */
function HealthPill({ label, value, ok, icon: Icon }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      background: ok ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)",
      border: `1px solid ${ok ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}`,
      borderRadius: "var(--radius)", padding: "8px 16px",
    }}>
      {Icon && (
        <Icon
          size={14}
          strokeWidth={2}
          color={ok ? "#22c55e" : "#ef4444"}
          style={{ flexShrink: 0 }}
        />
      )}
      <span style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500 }}>{label}:</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: ok ? "#22c55e" : "#ef4444" }}>{value}</span>
    </div>
  );
}

/* ── Activity Row ── */
function ActivityRow({ log, last }) {
  const [hovered, setHovered] = useState(false);
  const ts = log.timestamp || log.createdAt;
  return (
    <div
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "13px 20px",
        borderBottom: !last ? "1px solid var(--border)" : "none",
        background: hovered ? "rgba(26,138,138,0.05)" : "transparent",
        transition: "background 0.15s",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 8, height: 8, borderRadius: "50%",
          background: "var(--teal-400)", flexShrink: 0,
        }} />
        <div>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--gold-400)" }}>{log.action}</span>
          <span style={{ fontSize: 12, color: "var(--text-muted)", marginLeft: 8 }}>· {log.entityType}</span>
        </div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <p style={{ fontSize: 12, color: "var(--text-secondary)" }}>{log.adminUsername}</p>
        <p style={{ fontSize: 11, color: "var(--text-muted)" }}>
          {ts ? new Date(ts).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata", dateStyle: "short", timeStyle: "short",
          }) : ""}
        </p>
      </div>
    </div>
  );
}

/* ── Custom Date Picker ── */
function CustomDatePicker({ onApply, onClose }) {
  const today = todayIST();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(today);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  const apply = () => {
    if (!from || !to) return;
    if (from > to) { showToast("Start date must be before end date", "error"); return; }
    onApply({ startDate: from, endDate: to });
  };

  const labelStyle = { fontSize: 11, color: "var(--text-muted)", marginBottom: 4, display: "block" };
  const inputStyle = {
    padding: "7px 10px", borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg-card)", color: "var(--text-primary)", fontSize: 13,
    width: "100%", fontFamily: "var(--font-body)", cursor: "pointer",
  };

  return (
    <div ref={ref} style={{
      position: "absolute", top: "calc(100% + 8px)", left: 0, zIndex: 100,
      background: "var(--bg-card)", border: "1px solid var(--border)",
      borderRadius: 12, padding: 16, boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
      minWidth: 280,
    }}>
      <p style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", marginBottom: 12 }}>
        Custom date range
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>From</label>
          <input type="date" value={from} max={today}
            onChange={e => setFrom(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>To</label>
          <input type="date" value={to} max={today} min={from}
            onChange={e => setTo(e.target.value)} style={inputStyle} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button className="tfbtn" onClick={onClose}>Cancel</button>
        <button className="tfbtn on" onClick={apply} disabled={!from || !to}>Apply</button>
      </div>
    </div>
  );
}

const PRESET_FILTERS = ["Today", "Yesterday", "This Week", "This Month", "All Time"];

/* ════════════════════════════════════════════
Dashboard
════════════════════════════════════════════ */
export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("Today");
  const [customRange, setCustomRange] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (f = "All Time", custom = null) => {
    setLoading(true);
    try {
      const { startDate, endDate } = getRange(f, custom);
      const backendFilter = toBackendFilter(f);
      const res = await dashboardApi.getStats(startDate, endDate, backendFilter);
      setStats(res.data.data);
    } catch (e) {
      console.error("Dashboard load error", e);
      showToast("Failed to load dashboard", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load("Today"); }, [load]);

  const handlePreset = (f) => {
    setFilter(f);
    setCustomRange(null);
    setShowPicker(false);
    load(f);
  };

  const handleCustomApply = (range) => {
    setFilter("Custom");
    setCustomRange(range);
    setShowPicker(false);
    load("Custom", range);
  };

  const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("en-IN");
  const fmtRs = (n) => (n == null || n === undefined)
    ? "₹0"
    : `₹${Number(n).toLocaleString("en-IN")}`;

  const rev = stats?.revenue ?? {};
  const ord = stats?.orders ?? {};
  const usr = stats?.users ?? {};
  const wal = stats?.wallets ?? {};
  const con = stats?.content ?? {};
  const sys = stats?.systemHealth ?? {};

  const isAllTime = filter === "All Time";

  const showGrowth = rev.growthPercent != null && !isAllTime && filter !== "Custom";
  const growthValue = rev.growthPercent ?? 0;
  const growthPositive = growthValue >= 0;

  const revForFilter = () => {
    if (filter === "Custom") return fmtRs(ord.totalRevenue ?? 0);
    const map = {
      "Today": rev.today,
      "Yesterday": rev.yesterday,
      "This Week": rev.thisWeek,
      "This Month": rev.thisMonth,
      "All Time": rev.allTime,
    };
    return fmtRs(map[filter] ?? rev.allTime);
  };

  const filterLabel = () => {
    if (filter !== "Custom" || !customRange) return filter;
    const f = (s) => new Date(s + "T00:00:00+05:30").toLocaleDateString("en-IN", {
      day: "numeric", month: "short", timeZone: "Asia/Kolkata",
    });
    return `${f(customRange.startDate)} – ${f(customRange.endDate)}`;
  };

  const revBreakdown = [
    { label: "Today", value: fmtRs(rev.today), key: "Today" },
    { label: "Yesterday", value: fmtRs(rev.yesterday), key: "Yesterday" },
    { label: "This Week", value: fmtRs(rev.thisWeek), key: "This Week" },
    { label: "This Month", value: fmtRs(rev.thisMonth), key: "This Month" },
    { label: "All Time", value: fmtRs(rev.allTime), key: "All Time" },
  ];

  const recent = stats?.recentActivity ?? [];

  if (loading && !stats) return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      height: "60vh", flexDirection: "column", gap: 12,
    }}>
      <Star size={40} strokeWidth={1.5} color="#0F3D3E" />
      <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Loading dashboard…</p>
    </div>
  );

  return (
    <div style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}>
      <style>{`
        @keyframes fadeIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .ds { animation:fadeIn 0.35s ease both; }
        .tfbtn { padding:7px 16px; border-radius:20px; font-size:12px; font-weight:500;
          cursor:pointer; border:1px solid var(--border); transition:all 0.15s;
          font-family:var(--font-body); white-space:nowrap; }
        .tfbtn.on { background:linear-gradient(135deg,#1a8a8a,#25b0a8);
          border-color:transparent; color:#fff; box-shadow:0 2px 12px rgba(26,138,138,0.4); }
        .tfbtn:not(.on) { background:transparent; color:var(--text-secondary); }
        .tfbtn:not(.on):hover { border-color:#0F3D3E; color:#0F3D3E; }
        .tfbtn:disabled { opacity:0.5; cursor:not-allowed; }
        .tfbtn.custom-btn { border-style:dashed; }
        .rev-pill { text-align:center; padding:8px 16px; border-radius:10px;
          transition:background 0.15s; cursor:default; }
        .rev-pill.active { background:rgba(255,255,255,0.15); }
      `}</style>

      {/* ── Revenue Header ── */}
      <div style={{ marginBottom: 8 }}>
        <h2 style={{
          fontFamily: "var(--font-display)", fontSize: 15, color: "#0F3D3E",
          marginBottom: 14, paddingLeft: 12, borderLeft: "4px solid #0F3D3E",
        }}>
          Revenue Overview
        </h2>
      </div>

      {/* ── Filter Buttons ── */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
        {PRESET_FILTERS.map(f => (
          <button
            key={f}
            className={`tfbtn${filter === f ? " on" : ""}`}
            onClick={() => handlePreset(f)}
            disabled={loading}
          >
            {f}
          </button>
        ))}
        <div style={{ position: "relative" }}>
          <button
            className={`tfbtn custom-btn${filter === "Custom" ? " on" : ""}`}
            onClick={() => setShowPicker(v => !v)}
            disabled={loading}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <CalendarDays size={13} strokeWidth={2} />
            {filter === "Custom" ? filterLabel() : "Custom range"}
          </button>
          {showPicker && (
            <CustomDatePicker
              onApply={handleCustomApply}
              onClose={() => setShowPicker(false)}
            />
          )}
        </div>
      </div>

      {/* ── Revenue Banner ── */}
      <div className="ds" style={{
        background: "linear-gradient(135deg, #0F3D3E, #1a5c5e)",
        borderRadius: 16, padding: "22px 28px", marginBottom: 32, color: "#fff",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 16,
      }}>
        <div>
          <div style={{
            fontSize: 12, opacity: 0.75, textTransform: "uppercase",
            letterSpacing: "0.6px", marginBottom: 6,
          }}>
            {filterLabel()} Revenue
          </div>
          <div style={{ fontSize: 38, fontWeight: 700, fontFamily: "var(--font-display)" }}>
            {loading ? "…" : revForFilter()}
          </div>

          {/* Growth pill — only for presets with a prior period */}
          {showGrowth && (
            <div style={{ fontSize: 12, marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: growthPositive ? "rgba(134,239,172,0.2)" : "rgba(252,165,165,0.2)",
                border: `1px solid ${growthPositive
                  ? "rgba(134,239,172,0.4)" : "rgba(252,165,165,0.4)"}`,
                borderRadius: 20, padding: "2px 10px",
                color: growthPositive ? "#86efac" : "#fca5a5",
                fontWeight: 700, fontSize: 13,
              }}>
                <TrendingUp size={12} strokeWidth={2.5} />
                {growthPositive ? "+" : ""}{growthValue}%
              </span>
              <span style={{ opacity: 0.6, fontSize: 11 }}>{growthLabel(filter)}</span>
            </div>
          )}
        </div>

        {/* Revenue breakdown pills */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {revBreakdown.map(({ label, value, key }) => (
            <div key={key} className={`rev-pill${filter === key ? " active" : ""}`}>
              <div style={{
                fontSize: 11, opacity: filter === key ? 1 : 0.65,
                marginBottom: 4, fontWeight: filter === key ? 700 : 400,
              }}>
                {label}
              </div>
              <div style={{
                fontSize: filter === key ? 17 : 14,
                fontWeight: 700, opacity: filter === key ? 1 : 0.8,
              }}>
                {loading ? "…" : value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ══════════ ORDERS ══════════ */}
      <div className="ds" style={{ animationDelay: "0.04s", marginBottom: 24 }}>
        <SectionTitle>Orders — {filterLabel()}</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 14 }}>
          {isAllTime ? (
            <>
              <StatCard icon={<LayoutList size={20} strokeWidth={2} />} title="Total Orders"
                value={loading ? "…" : fmt(ord.total)}
                gradient="linear-gradient(135deg,rgba(15,61,62,0.85),rgba(15,61,62,0.5))" loading={loading} />
              <StatCard icon={<CheckCircle2 size={20} strokeWidth={2} />} title="Successful"
                value={loading ? "…" : fmt(ord.success)}
                gradient="linear-gradient(135deg,rgba(34,197,94,0.85),rgba(34,197,94,0.55))" loading={loading} />
              <StatCard icon={<Clock size={20} strokeWidth={2} />} title="Created"
                value={loading ? "…" : fmt(ord.created)}
                gradient="linear-gradient(135deg,rgba(59,130,246,0.85),rgba(59,130,246,0.55))" loading={loading} />
              <StatCard icon={<XCircle size={20} strokeWidth={2} />} title="Failed"
                value={loading ? "…" : fmt(ord.failed)}
                gradient="linear-gradient(135deg,rgba(239,68,68,0.85),rgba(239,68,68,0.55))" loading={loading} />
              <StatCard icon={<DollarSign size={20} strokeWidth={2} />} title="All-Time Revenue"
                value={loading ? "…" : fmtRs(rev.allTime)}
                gradient="linear-gradient(135deg,rgba(15,61,62,0.85),rgba(45,120,120,0.55))" loading={loading} />
            </>
          ) : (
            <>
              <StatCard icon={<ShoppingCart size={20} strokeWidth={2} />} title="Orders Placed"
                value={loading ? "…" : fmt(ord.total)}
                gradient="linear-gradient(135deg,rgba(15,61,62,0.85),rgba(15,61,62,0.5))" loading={loading} />
              <StatCard icon={<CheckCircle2 size={20} strokeWidth={2} />} title="Successful"
                value={loading ? "…" : fmt(ord.success)}
                gradient="linear-gradient(135deg,rgba(34,197,94,0.85),rgba(34,197,94,0.55))" loading={loading} />
              <StatCard icon={<Clock size={20} strokeWidth={2} />} title="Created"
                value={loading ? "…" : fmt(ord.created)}
                gradient="linear-gradient(135deg,rgba(59,130,246,0.85),rgba(59,130,246,0.55))" loading={loading} />
              <StatCard icon={<XCircle size={20} strokeWidth={2} />} title="Failed"
                value={loading ? "…" : fmt(ord.failed)}
                gradient="linear-gradient(135deg,rgba(239,68,68,0.85),rgba(239,68,68,0.55))" loading={loading} />
              <StatCard icon={<Banknote size={20} strokeWidth={2} />} title="Period Revenue"
                value={loading ? "…" : fmtRs(ord.totalRevenue ?? 0)}
                gradient="linear-gradient(135deg,rgba(15,61,62,0.85),rgba(45,120,120,0.55))" loading={loading} />
            </>
          )}
        </div>
      </div>

      {/* ══════════ CUSTOMERS ══════════ */}
      <div className="ds" style={{ animationDelay: "0.08s", marginBottom: 24 }}>
        <SectionTitle>Customers — {filterLabel()}</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 14 }}>
          {isAllTime ? (
            <>
              <StatCard icon={<Users size={20} strokeWidth={2} />} title="Total Users"
                value={loading ? "…" : fmt(usr.total)}
                gradient="linear-gradient(135deg,rgba(15,61,62,0.85),rgba(15,61,62,0.55))" loading={loading} />
              <StatCard icon={<UserCheck size={20} strokeWidth={2} />} title="Active"
                value={loading ? "…" : fmt(usr.sourceAstro + usr.sourceInd - usr.inactive)}
                gradient="linear-gradient(135deg,rgba(34,197,94,0.85),rgba(34,197,94,0.55))" loading={loading} />
              <StatCard icon={<UserX size={20} strokeWidth={2} />} title="Inactive"
                value={loading ? "…" : fmt(usr.inactive)}
                gradient="linear-gradient(135deg,rgba(239,68,68,0.85),rgba(239,68,68,0.55))" loading={loading} />
              <StatCard icon={<Star size={20} strokeWidth={2} />} title="Source ASTRO"
                value={loading ? "…" : fmt(usr.sourceAstro)}
                gradient="linear-gradient(135deg,rgba(208,179,106,0.85),rgba(208,179,106,0.55))" loading={loading} />
              <StatCard icon={<Smartphone size={20} strokeWidth={2} />} title="Source IND"
                value={loading ? "…" : fmt(usr.sourceInd)}
                gradient="linear-gradient(135deg,rgba(59,130,246,0.85),rgba(59,130,246,0.55))" loading={loading} />
            </>
          ) : (
            <>
              <StatCard icon={<UserPlus size={20} strokeWidth={2} />} title="New Signups"
                value={loading ? "…" : fmt(usr.newInPeriod)}
                gradient="linear-gradient(135deg,rgba(34,197,94,0.85),rgba(34,197,94,0.55))" loading={loading} />
              <StatCard icon={<Star size={20} strokeWidth={2} />} title="Source ASTRO"
                value={loading ? "…" : fmt(usr.sourceAstroPeriod)}
                gradient="linear-gradient(135deg,rgba(208,179,106,0.85),rgba(208,179,106,0.55))" loading={loading} />
              <StatCard icon={<Smartphone size={20} strokeWidth={2} />} title="Source IND"
                value={loading ? "…" : fmt(usr.sourceIndPeriod)}
                gradient="linear-gradient(135deg,rgba(59,130,246,0.85),rgba(59,130,246,0.55))" loading={loading} />
            </>
          )}
        </div>
      </div>

      {/* ══════════ WALLETS ══════════ */}
      <div className="ds" style={{ animationDelay: "0.12s", marginBottom: 24 }}>
        <SectionTitle>Wallets — {isAllTime ? "Snapshot" : `Activity (${filterLabel()})`}</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 14 }}>
          {isAllTime ? (
            <>
              <StatCard icon={<Wallet size={20} strokeWidth={2} />} title="Total Wallets"
                value={loading ? "…" : fmt(wal.totalWallets)}
                gradient="linear-gradient(135deg,rgba(15,61,62,0.85),rgba(15,61,62,0.55))" loading={loading} />
              <StatCard icon={<PiggyBank size={20} strokeWidth={2} />} title="Total Balance" sub="Credits held"
                value={loading ? "…" : fmt(wal.totalBalance)}
                gradient="linear-gradient(135deg,rgba(208,179,106,0.85),rgba(208,179,106,0.55))" loading={loading} />
              <StatCard icon={<CheckCircle2 size={20} strokeWidth={2} />} title="With Balance"
                value={loading ? "…" : fmt(wal.walletsWithBalance)}
                gradient="linear-gradient(135deg,rgba(34,197,94,0.85),rgba(34,197,94,0.55))" loading={loading} />
            </>
          ) : (
            <>
              <StatCard icon={<ArrowUpCircle size={20} strokeWidth={2} />} title="Credit Tx"
                value={loading ? "…" : fmt(wal.totalCreditTx)}
                gradient="linear-gradient(135deg,rgba(34,197,94,0.85),rgba(34,197,94,0.55))" loading={loading} />
              <StatCard icon={<ArrowDownCircle size={20} strokeWidth={2} />} title="Debit Tx"
                value={loading ? "…" : fmt(wal.totalDebitTx)}
                gradient="linear-gradient(135deg,rgba(239,68,68,0.85),rgba(239,68,68,0.55))" loading={loading} />
              <StatCard icon={<Gift size={20} strokeWidth={2} />} title="Credits Issued"
                value={loading ? "…" : fmt(wal.creditsIssued)}
                gradient="linear-gradient(135deg,rgba(34,197,94,0.45),rgba(34,197,94,0.18))" loading={loading} />
              <StatCard icon={<Banknote size={20} strokeWidth={2} />} title="Credits Spent"
                value={loading ? "…" : fmt(wal.creditsSpent)}
                gradient="linear-gradient(135deg,rgba(245,158,11,0.5),rgba(245,158,11,0.2))" loading={loading} />
            </>
          )}
        </div>
      </div>

      {/* ══════════ CONTENT ══════════ */}
      <div className="ds" style={{ animationDelay: "0.14s", marginBottom: 24 }}>
        <SectionTitle>Content (All Time)</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 14 }}>
          <StatCard icon={<CircleDot size={20} strokeWidth={2} />} title="Total Zodiacs"
            value={fmt(con.totalZodiacs)}
            gradient="linear-gradient(135deg,rgba(208,179,106,0.85),rgba(208,179,106,0.55))" loading={loading} />
          <StatCard icon={<Sparkles size={20} strokeWidth={2} />} title="Active Zodiacs"
            value={fmt(con.activeZodiacs)}
            gradient="linear-gradient(135deg,rgba(34,197,94,0.85),rgba(34,197,94,0.55))" loading={loading} />
          <StatCard icon={<Moon size={20} strokeWidth={2} />} title="Horoscopes"
            value={fmt(con.totalHoroscopes)}
            gradient="linear-gradient(135deg,rgba(167,139,250,0.85),rgba(167,139,250,0.55))" loading={loading} />
          <StatCard icon={<CreditCard size={20} strokeWidth={2} />} title="Credit Packs"
            value={fmt(con.totalCreditPacks)}
            gradient="linear-gradient(135deg,rgba(208,179,106,0.75),rgba(208,179,106,0.4))" loading={loading} />
        </div>
      </div>

      {/* ══════════ SYSTEM HEALTH ══════════ */}
      <div className="ds" style={{ animationDelay: "0.16s", marginBottom: 24 }}>
        <SectionTitle>System Health</SectionTitle>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <HealthPill
            label="System Status"
            value={sys.status ?? "—"}
            ok={sys.status === "HEALTHY"}
            icon={Activity}
          />
          <HealthPill
            label="Database"
            value={sys.dbConnected ? "Connected" : "Disconnected"}
            ok={sys.dbConnected}
            icon={Database}
          />
          <HealthPill
            label="Redis"
            value={sys.redisConnected ? "Connected" : "Disconnected"}
            ok={sys.redisConnected}
            icon={Server}
          />
        </div>
      </div>

      {/* ══════════ RECENT ACTIVITY ══════════ */}
      <div className="ds" style={{ animationDelay: "0.18s" }}>
        <SectionTitle>Recent Activity</SectionTitle>
        <div style={{
          background: "var(--bg-card)", border: "1px solid var(--border)",
          borderRadius: 14, overflow: "hidden",
        }}>
          {recent.length === 0 ? (
            <p style={{ padding: "24px", color: "var(--text-muted)", fontSize: 13 }}>
              No recent activity.
            </p>
          ) : recent.map((log, i) => (
            <ActivityRow key={i} log={log} last={i === recent.length - 1} />
          ))}
        </div>
      </div>
    </div>
  );
}